import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { useEngagements } from '../lib/queries'
import { Empty, Money, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDate } from '../lib/format'

interface ItemRow {
  id: string
  sku: string
  name: string
  unit_cost_satang: number
  reorder_level: number
  active: boolean
  qty_on_hand: number
  last_counted_at?: string
  variance_qty: number
  variance_value_satang: number
  count_count: number
  created_at: string
  updated_at: string
}

interface ScanResult {
  engagement_id: string
  items: number
  counts: number
  write_offs: number
  hits: number
  inserted: number
  skipped: number
  by_rule: Record<string, number>
  elapsed_ms: number
}

interface ItemDraft {
  id: string
  sku: string
  name: string
  unitCost: string
  reorderLevel: string
  active: boolean
}

interface CountDraft {
  countedAt: string
  qtyOnHand: string
  varianceQty: string
  note: string
}

const EMPTY_ITEM: ItemDraft = { id: '', sku: '', name: '', unitCost: '', reorderLevel: '0', active: true }

function todayISO(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

const EMPTY_COUNT: CountDraft = { countedAt: todayISO(), qtyOnHand: '', varianceQty: '0', note: '' }

const RULE_LABEL: Record<string, string> = {
  INVENTORY_VARIANCE: 'ส่วนต่างจากการนับ',
  INVENTORY_UNCOUNTED: 'ไม่ได้นับเกิน 90 วัน',
}

// bahtToSatang keeps money in integer satang end to end. The input is a decimal string, so
// 12.35 becomes 1235 without ever passing through a float that could round down a satang.
function bahtToSatang(v: string): number {
  const s = v.trim().replace(/,/g, '')
  if (s === '') return 0
  const n = Number(s)
  if (!Number.isFinite(n) || n < 0) return -1
  return Math.round(n * 100)
}

export function Inventory() {
  const { me } = useAuth()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager' || me?.role === 'auditor'
  const qc = useQueryClient()

  const q = useQuery({ queryKey: ['inventory-items'], queryFn: () => api<ItemRow[]>('/v1/inventory/items') })
  const engagements = useEngagements()
  const engagementRows = engagements.data ?? []

  const [itemOpen, setItemOpen] = useState(false)
  const [itemDraft, setItemDraft] = useState<ItemDraft>(EMPTY_ITEM)
  const [countFor, setCountFor] = useState<ItemRow | null>(null)
  const [countDraft, setCountDraft] = useState<CountDraft>(EMPTY_COUNT)
  const [scan, setScan] = useState<ScanResult | null>(null)
  const [engagementId, setEngagementId] = useState('')
  const [err, setErr] = useState('')

  const activeEngagement = engagementId || engagementRows[0]?.id || ''

  const saveItem = useMutation({
    mutationFn: (d: ItemDraft) => apiWrite<ItemRow>('/v1/inventory/items', 'POST', {
      id: d.id,
      sku: d.sku,
      name: d.name,
      unit_cost_satang: bahtToSatang(d.unitCost),
      reorder_level: Number(d.reorderLevel) || 0,
      active: d.active,
    }),
    onSuccess: () => {
      setItemOpen(false)
      setItemDraft(EMPTY_ITEM)
      setErr('')
      void qc.invalidateQueries({ queryKey: ['inventory-items'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  const saveCount = useMutation({
    mutationFn: (args: { item: ItemRow; draft: CountDraft }) => apiWrite('/v1/inventory/items/' + args.item.id + '/counts', 'POST', {
      counted_at: args.draft.countedAt,
      qty_on_hand: Number(args.draft.qtyOnHand) || 0,
      variance_qty: Number(args.draft.varianceQty) || 0,
      note: args.draft.note,
    }),
    onSuccess: () => {
      setCountFor(null)
      setCountDraft(EMPTY_COUNT)
      setErr('')
      void qc.invalidateQueries({ queryKey: ['inventory-items'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  const runScan = useMutation({
    mutationFn: (id: string) => apiWrite<ScanResult>('/v1/inventory/scan', 'POST', { engagement_id: id }),
    onSuccess: (res) => {
      setScan(res)
      setErr('')
    },
    onError: (e) => setErr((e as Error).message),
  })

  function startCreate() {
    if (itemOpen && itemDraft.id === '') {
      setItemOpen(false)
      return
    }
    setItemDraft(EMPTY_ITEM)
    setErr('')
    setItemOpen(true)
  }

  function startEdit(it: ItemRow) {
    setItemDraft({
      id: it.id,
      sku: it.sku,
      name: it.name,
      unitCost: (it.unit_cost_satang / 100).toFixed(2),
      reorderLevel: String(it.reorder_level),
      active: it.active,
    })
    setErr('')
    setItemOpen(true)
  }

  function startCount(it: ItemRow) {
    setCountFor(it)
    setCountDraft({ ...EMPTY_COUNT, countedAt: todayISO() })
    setErr('')
  }

  const rows = q.data ?? []
  const totalValue = rows.reduce((sum, it) => sum + it.qty_on_hand * it.unit_cost_satang, 0)
  const varianceRows = rows.filter((it) => it.variance_qty !== 0)

  return (
    <>
      <PageHead
        title="ทะเบียนสินค้าคงคลัง"
        sub="สินค้าคงเหลือ การนับจริง และส่วนต่างที่ต้องสอบทาน พร้อมเครื่องยนต์ตรวจ shrinkage จากหลักฐานในบัญชี"
        actions={canWrite ? (
          <button className="btn btn-primary" onClick={startCreate}>
            {itemOpen ? 'ปิดฟอร์ม' : 'เพิ่มสินค้า'}
          </button>
        ) : undefined}
      />

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="รายการสินค้าทั้งหมด" value={number(rows.length)} unit="รายการ"
          foot={`ใช้งานอยู่ ${rows.filter((it) => it.active).length} รายการ`} />
        <Stat label="มูลค่าสินค้าตามการนับล่าสุด" value={<Money satang={totalValue} compact />}
          foot="คำนวณจากจำนวนที่นับได้ × ต้นทุนต่อหน่วย" />
        <Stat label="รายการที่มีส่วนต่าง" value={number(varianceRows.length)} unit="รายการ"
          foot="รวมทุกรายการที่ส่วนต่างไม่เป็นศูนย์" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="card-label">ตรวจ shrinkage กับโครงการตรวจสอบ</div>
        <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <select aria-label="กรองตามประเภทสินค้า" className="select" value={activeEngagement} style={{ minWidth: 300 }}
            onChange={(e) => { setEngagementId(e.target.value); setScan(null) }}>
            {engagementRows.length === 0 && <option value="">ยังไม่มีโครงการตรวจสอบ</option>}
            {engagementRows.map((e) => (
              <option key={e.id} value={e.id}>{e.code} · {e.title}</option>
            ))}
          </select>
          <button className="btn btn-primary"
            disabled={!activeEngagement || !canWrite || runScan.isPending}
            onClick={() => { setErr(''); runScan.mutate(activeEngagement) }}>
            {runScan.isPending ? 'กำลังสแกน…' : 'สแกนสินค้าคงคลัง'}
          </button>
          {!canWrite && <span className="dim" style={{ fontSize: 11.5 }}>บทบาทของคุณอ่านได้อย่างเดียว</span>}
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
          เกณฑ์: ส่วนต่างมูลค่าตั้งแต่ 100,000 บาท หรือตั้งแต่ 15% ของจำนวนที่นับได้ และสินค้าที่ยังใช้งานแต่ไม่ได้นับเกิน 90 วัน
        </div>

        {scan && (
          <div style={{ marginTop: 18 }}>
            <div className="grid-4">
              <div>
                <div className="card-label">อ่านรายการสินค้า</div>
                <div className="stat-value">{number(scan.items)}<span className="stat-unit">รายการ</span></div>
              </div>
              <div>
                <div className="card-label">ประวัติการนับ</div>
                <div className="stat-value">{number(scan.counts)}<span className="stat-unit">ครั้ง</span></div>
                <div className="stat-foot">รายการตัดจำหน่าย {scan.write_offs} เดือน</div>
              </div>
              <div>
                <div className="card-label">ข้อสังเกตที่พบ</div>
                <div className="stat-value">{number(scan.hits)}<span className="stat-unit">เรื่อง</span></div>
                <div className="stat-foot">ใช้เวลา {scan.elapsed_ms} ms</div>
              </div>
              <div>
                <div className="card-label">บันทึกใหม่</div>
                <div className="stat-value">{number(scan.inserted)}<span className="stat-unit">เรื่อง</span></div>
                <div className="stat-foot">ข้ามที่มีอยู่แล้ว {scan.skipped} เรื่อง</div>
              </div>
            </div>
            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {Object.keys(scan.by_rule).length === 0 && (
                <span className="dim" style={{ fontSize: 12 }}>ไม่พบข้อสังเกตจากข้อมูลปัจจุบัน</span>
              )}
              {Object.entries(scan.by_rule).map(([code, n]) => (
                <span key={code} className="chip">{RULE_LABEL[code] ?? code} · {n}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {itemOpen && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">{itemDraft.id ? `แก้ไขสินค้า · ${itemDraft.sku}` : 'เพิ่มสินค้าใหม่'}</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="i-sku">รหัสสินค้า (SKU)</label>
              <input id="i-sku" className="input mono" value={itemDraft.sku} maxLength={40}
                onChange={(e) => setItemDraft({ ...itemDraft, sku: e.target.value.toUpperCase() })}
                placeholder="SKU-1001" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="i-name">ชื่อสินค้า</label>
              <input id="i-name" className="input" value={itemDraft.name}
                onChange={(e) => setItemDraft({ ...itemDraft, name: e.target.value })}
                placeholder="ปูนซีเมนต์ผสม 50 กก." />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="i-cost">ต้นทุนต่อหน่วย (บาท)</label>
              <input id="i-cost" className="input mono" inputMode="decimal" value={itemDraft.unitCost}
                onChange={(e) => setItemDraft({ ...itemDraft, unitCost: e.target.value.replace(/[^\d.]/g, '') })}
                placeholder="185.00" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="i-reorder">จุดสั่งซื้อซ้ำ (หน่วย)</label>
              <input id="i-reorder" className="input mono" inputMode="numeric" value={itemDraft.reorderLevel}
                onChange={(e) => setItemDraft({ ...itemDraft, reorderLevel: e.target.value.replace(/\D/g, '') })}
                placeholder="100" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="i-active">สถานะการใช้งาน</label>
              <select id="i-active" className="select" style={{ width: '100%' }} value={itemDraft.active ? '1' : '0'}
                onChange={(e) => setItemDraft({ ...itemDraft, active: e.target.value === '1' })}>
                <option value="1">ใช้งาน</option>
                <option value="0">เลิกใช้</option>
              </select>
            </div>
          </div>
          {err && <div className="form-error">{err}</div>}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary"
              disabled={saveItem.isPending || itemDraft.sku === '' || itemDraft.name === '' || bahtToSatang(itemDraft.unitCost) < 0}
              onClick={() => { setErr(''); saveItem.mutate(itemDraft) }}>
              {saveItem.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              ต้นทุนเก็บเป็นสตางค์ (จำนวนเต็ม) เพื่อไม่ให้ยอดรวมเพี้ยนจากการปัดเศษ
            </span>
          </div>
        </div>
      )}

      {countFor && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">บันทึกการนับ · {countFor.sku} {countFor.name}</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-date">วันที่นับ</label>
              <input id="c-date" className="input mono" type="date" value={countDraft.countedAt}
                onChange={(e) => setCountDraft({ ...countDraft, countedAt: e.target.value })} />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-qty">จำนวนที่นับได้ (หน่วย)</label>
              <input id="c-qty" className="input mono" inputMode="numeric" value={countDraft.qtyOnHand}
                onChange={(e) => setCountDraft({ ...countDraft, qtyOnHand: e.target.value.replace(/\D/g, '') })}
                placeholder="480" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-var">ส่วนต่างจากบัญชี (หน่วย, ติดลบ = ของขาด)</label>
              <input id="c-var" className="input mono" inputMode="text" value={countDraft.varianceQty}
                onChange={(e) => setCountDraft({ ...countDraft, varianceQty: e.target.value.replace(/[^\d-]/g, '') })}
                placeholder="-120" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-note">หมายเหตุ</label>
              <input id="c-note" className="input" value={countDraft.note} maxLength={500}
                onChange={(e) => setCountDraft({ ...countDraft, note: e.target.value })}
                placeholder="นับโดยทีมคลังสินค้า ตรวจสอบรอบที่ 2" />
            </div>
          </div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
            จำนวนตามบัญชี = จำนวนที่นับได้ − ส่วนต่าง · นับซ้ำในวันเดียวกันจะแก้ไขผลการนับเดิม ไม่สร้างรายการใหม่
          </div>
          {err && <div className="form-error">{err}</div>}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary"
              disabled={saveCount.isPending || countDraft.qtyOnHand === '' || countDraft.countedAt === ''}
              onClick={() => { setErr(''); saveCount.mutate({ item: countFor, draft: countDraft }) }}>
              {saveCount.isPending ? 'กำลังบันทึก…' : 'บันทึกการนับ'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setCountFor(null); setErr('') }}>ยกเลิก</button>
          </div>
        </div>
      )}

      {q.isLoading ? <Skel h={240} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีรายการสินค้า" body="เพิ่มสินค้ารายการแรก แล้วบันทึกการนับเพื่อให้เครื่องยนต์ตรวจส่วนต่างได้" />
      ) : (
        <div className="table-wrap card">
          <table className="data">
            <thead>
              <tr>
                <th>รหัสสินค้า</th>
                <th>ชื่อสินค้า</th>
                <th className="num">ต้นทุน/หน่วย</th>
                <th className="num">คงเหลือตามการนับ</th>
                <th>นับล่าสุด</th>
                <th className="num">ส่วนต่าง</th>
                <th className="num">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.id}>
                  <td className="mono">{it.sku}</td>
                  <td>
                    <div>{it.name}</div>
                    {!it.active && <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>เลิกใช้แล้ว</div>}
                  </td>
                  <td className="num"><Money satang={it.unit_cost_satang} /></td>
                  <td className="num">
                    {it.count_count === 0 ? <span className="dim">ยังไม่นับ</span> : number(it.qty_on_hand)}
                    {it.reorder_level > 0 && it.count_count > 0 && it.qty_on_hand <= it.reorder_level && (
                      <div className="dim" style={{ fontSize: 11, marginTop: 3, color: 'var(--color-amber)' }}>
                        ต่ำกว่าจุดสั่งซื้อ {number(it.reorder_level)}
                      </div>
                    )}
                  </td>
                  <td className="mono dim">{it.last_counted_at ? thaiDate(it.last_counted_at) : '—'}</td>
                  <td className="num">
                    {it.variance_qty === 0 ? <span className="dim">—</span> : (
                      <>
                        <span className="tnum" style={{ color: it.variance_qty < 0 ? 'var(--color-critical)' : 'var(--color-amber)' }}>
                          {it.variance_qty > 0 ? '+' : ''}{number(it.variance_qty)}
                        </span>
                        <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                          <Money satang={Math.abs(it.variance_value_satang)} />
                        </div>
                      </>
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {canWrite && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => startCount(it)}>บันทึกการนับ</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(it)}>แก้ไข</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {err && !itemOpen && !countFor && <div className="form-error">{err}</div>}
    </>
  )
}
